import styled, { css } from 'styled-components';
import { useCallback, useState } from 'react';
import type { ChipSize } from 'common/ui/Chip';
import Tooltip from 'common/ui/Tooltip';
import { MacroChip } from 'domains/reporter/Macro/components';
import { useCompactUI } from 'hooks/useCompactUI';
import { ReactEditor, Transforms, useSlateStatic } from '../../../core';
import type { RenderElementProps } from '../../../core';
import type { MacroPlaceholderPluginElement } from '../types';
import { getMacroPlaceholderDisplayName, removeMacroPlaceholderAtPath } from '../utils';
import { useDeferredMacroPreview } from '../useDeferredMacroPreview';
import { MacroPlaceholderChipPreview } from './MacroPlaceholderChipPreview';

const tooltipContentCss = css`
  padding: 0;
  border-radius: 0;
  background: transparent;
`;
const PREVIEW_COMPUTE_DELAY_MS = 100;
const TOOLTIP_OPEN_DELAY_MS = 300;

export type MacroPlaceholderChipElementProps = {
  attributes: RenderElementProps['attributes'];
  children: React.ReactNode;
  element: MacroPlaceholderPluginElement;
  ['data-editor-element']?: string;
  size?: ChipSize;
  selected?: boolean;
};

export const MacroPlaceholderChipElement = ({
  attributes,
  children,
  element,
  size = 'md',
  selected = false,
  ['data-editor-element']: dataEditorElement,
}: MacroPlaceholderChipElementProps): React.ReactElement => {
  const editor = useSlateStatic();
  const [isPreviewActive, setIsPreviewActive] = useState(false);
  const preview = useDeferredMacroPreview(element.referenceID, {
    isActive: isPreviewActive,
    loadDelayMs: PREVIEW_COMPUTE_DELAY_MS,
  });
  const macroPreviewData = preview.data;
  const macroDisplayName = macroPreviewData?.macroName ?? getMacroPlaceholderDisplayName(element);
  const { showCompactHeight } = useCompactUI();
  const isPreviewLoading = isPreviewActive && preview.status === 'loading';

  const setSlateWrapperRef = useCallback(
    (node: HTMLSpanElement | null) => {
      const slateElementRef = attributes.ref as React.Ref<HTMLSpanElement> | undefined;
      if (typeof slateElementRef === 'function') {
        slateElementRef(node);
        return;
      }
      if (slateElementRef != null && 'current' in slateElementRef) {
        (slateElementRef as React.MutableRefObject<HTMLSpanElement | null>).current = node;
      }
    },
    [attributes.ref]
  );

  const onRemove = () => {
    const path = ReactEditor.findPath(editor, element);
    removeMacroPlaceholderAtPath(editor, path);
  };

  // Use mousedown (not click) so we can prevent the chip from stealing focus from the editor
  // before Slate updates selection. This keeps editor hotkeys like Backspace/Delete working.
  const onMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    const path = ReactEditor.findPath(editor, element);
    Transforms.select(editor, path);
    ReactEditor.focus(editor);
  };

  const onTooltipTrigger = useCallback(() => {
    setIsPreviewActive(true);
  }, []);

  const onTooltipUntrigger = useCallback(() => {
    setIsPreviewActive(false);
  }, []);

  return (
    <MacroPlaceholderChipWrapper
      ref={setSlateWrapperRef}
      data-slate-node={attributes['data-slate-node']}
      data-slate-inline={attributes['data-slate-inline']}
      data-slate-void={attributes['data-slate-void']}
      dir={attributes.dir}
      data-testid="macro-placeholder-chip"
      data-editor-element={dataEditorElement}
    >
      <MacroPlaceholderChipSurface contentEditable={false}>
        <Tooltip
          trigger="mouseenter"
          delay={[TOOLTIP_OPEN_DELAY_MS, 0]}
          placement="top-start"
          appendTo={() => document.body}
          onTrigger={onTooltipTrigger}
          onUntrigger={onTooltipUntrigger}
          content={
            isPreviewLoading || macroPreviewData != null ? (
              <MacroPlaceholderChipPreview
                macroName={macroDisplayName}
                macroContent={macroPreviewData?.macroContent ?? null}
                isLoading={isPreviewLoading}
                compact={showCompactHeight}
              />
            ) : null
          }
          contentCss={tooltipContentCss}
        >
          <MacroPlaceholderChip
            size={size}
            isSelected={selected}
            onRemove={onRemove}
            onMouseDown={onMouseDown}
          >
            {macroDisplayName}
          </MacroPlaceholderChip>
        </Tooltip>
      </MacroPlaceholderChipSurface>
      <HiddenSlateChildren>{children}</HiddenSlateChildren>
    </MacroPlaceholderChipWrapper>
  );
};

const MacroPlaceholderChipWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const MacroPlaceholderChipSurface = styled.span`
  display: inline-flex;
  align-items: center;
`;

const MacroPlaceholderChip = styled(MacroChip)<{ size?: ChipSize }>`
  pointer-events: auto;
`;

const HiddenSlateChildren = styled.span`
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
`;
